'use client';

import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { Autocomplete } from 'src/components/PulseUI';
import ControlLabel from './ControlLabel';

interface ControlledMultiSelectProps {
  name: string;
  options: { value: string; label: string }[];
  label?: string;
  rules?: Record<string, unknown>;
  required?: boolean;
  placeholder?: string;
  helperText?: string;
  hasTooltip?: boolean;
  tooltipText?: string;
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  className?: string;
  'data-testid'?: string;
}

const ControlledMultiSelect = ({ ...props }: ControlledMultiSelectProps) => {
  const { control } = useFormContext();

  if (!props) return null;

  const errorMessage = props?.helperText || 'This field is required';
  const required = props?.required || !!props.rules?.required;
  const testId = props['data-testid'] || `multiselect-${props.name}`;
  const errorId = `${props.name}-error`;

  if (!props.options) return null;

  return (
    <Controller
      name={props.name}
      control={control}
      rules={{
        // An empty array is truthy, so `required` alone won't catch "nothing
        // selected" — validate the array length instead.
        validate: (value) =>
          !required ||
          (Array.isArray(value) && value.length > 0) ||
          errorMessage,
        ...props.rules,
      }}
      render={({ field, fieldState }) => {
        const selectedValues: string[] = field.value || [];
        // Read the error off fieldState, not formState.errors[name]: CRM callers
        // register a dotted name (`secondary_inputs.payer_types`) which RHF nests,
        // so indexing errors with the full string would never match.
        const hasError = !!fieldState.error;
        // SecondaryInput passes size="small" (not a PulseUI size); normalize to
        // the compact 'sm' the sibling fields use, unless a valid size is given.
        const size =
          props.size === 'md' || props.size === 'lg' ? props.size : 'sm';

        const handleRemove = (value: string) => {
          field.onChange(
            selectedValues.filter((current) => current !== value)
          );
        };

        // Match the neutral-300 border of the sibling modal inputs; leave the
        // primitive's error styling alone when the field is invalid.
        const borderClasses = hasError
          ? ''
          : 'border-neutral-300 hover:border-neutral-500';

        return (
          <ControlLabel
            label={props?.label || ''}
            required={required}
            hasTooltip={props.hasTooltip}
            tooltipText={props.tooltipText}
          >
            <div data-testid={testId}>
              <Autocomplete
                id={props.name}
                value={selectedValues}
                options={props.options || []}
                placeholder={props?.placeholder || 'Select...'}
                onChange={(newValue) => {
                  field.onChange(newValue);
                }}
                error={hasError}
                size={size}
                hideOptionsHeader
                disabled={props.disabled}
                // Trim the inner Input's double-padding and match the sibling fields' text size
                // without editing the shared Autocomplete primitive.
                className={`rounded-full [&>div]:py-0 [&>div]:pl-1 [&_input]:text-base ${borderClasses} ${
                  props.className || ''
                }`}
              />
            </div>
            {/* Selected values as removable chips below the input — mirrors the
                dominant FilterSets/SelectedFilters convention. */}
            {selectedValues.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mt-2">
                {selectedValues.map((value) => {
                  const label =
                    props.options?.find((option) => option.value === value)
                      ?.label || value;
                  return (
                    <div
                      key={value}
                      data-testid={`${testId}-chip-${value}`}
                      className="px-2 py-1 bg-neutral-300 rounded-2xl inline-flex justify-center items-center gap-1 overflow-hidden"
                    >
                      <div className="text-center justify-start text-primary text-xs font-medium leading-tight">
                        {label}
                      </div>
                      <button
                        type="button"
                        aria-label={`Remove ${label}`}
                        disabled={props.disabled}
                        onClick={() => handleRemove(value)}
                        className="w-3.5 h-3.5 rounded-full bg-primary flex items-center justify-center cursor-pointer"
                      >
                        <Icon
                          icon={faTimes}
                          size="xs"
                          className="text-neutral-300"
                        />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            {hasError && (
              <p
                id={errorId}
                role="alert"
                className="text-red-500 text-xs mt-1"
                data-testid={`${testId}-error`}
              >
                {fieldState.error?.message as string}
              </p>
            )}
          </ControlLabel>
        );
      }}
    />
  );
};

export default ControlledMultiSelect;
